import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import styled from "styled-components";
import { useHorizontalScroll } from "../../../utils/HorizontalScrollHook";
import { useTheme } from "../../../contexts/ThemeContext";
import LabelNumber from "../../../styled-components/LabelNumber";
import { ITransferHistory } from "../../../types/IInjuryData";
import { StyledHeader } from "../../ClubDetail/ClubFinancial/ClubFinancial";
import LabelText from "../../../ui/LabelText";
import { IPlayerTranfer } from "../../../types/IPlayerDetail";

const dummyTransferHistoryData: ITransferHistory[] = [
  {
    age: 0,
    isTransferred: true,
    playerTransfers: [
      {
        isFreeTransfer: false,
        loanStatus: "Start",
        teamId: 0,
        teamLogoURL: "",
        teamName: "",
        transferFeeValue: 0,
        transferFeeValueText: "",
        transferFeeValueInText: "",
      },
    ],
    season: "",
    seasonId: 0,
    teamId: 0,
    teamLogoURL: "",
    teamName: "",
    wagesPW: 0,
    wagesPWText: "",
  },
];
interface TransferChartProps {
  propData: ITransferHistory[];
  playerTransferData: IPlayerTranfer[];
}

export const ChartContainer = styled.div`
  position: relative;
  min-width: 80%;
  display: flex;
  border: 2px solid ${({ theme }) => theme.playerDataBorder};
  min-height: 220px;
  border-radius: 10px;
  background-color: transparent;
  font-family: "DM Mono", monospace;
  margin-top: 20px;
  margin-bottom: 40px;
  & .transfer-dotlabel-container {
    color: ${({ theme }) => theme.NumberColor};
  }
`;

export const SideBar = styled.div`
  max-width: 250px;
  min-width: 250px;
  z-index: 2;
  font-size: large;
  min-height: 100%;
  border-right: 1px solid ${({ theme }) => theme.playerDataBorder};
  box-shadow: 10px -1px 10px -5px rgba(0, 0, 0, 0.25);
`;

export const SidebarLower = styled.div`
  width: 90%;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: end;
  margin-left: 12px;
  padding-top: 24px;
  padding-bottom: 24px;
  > span:nth-child(2) {
    padding-top: 16px;
  }
  > span:last-child {
    padding-top: 24px;
  }
`;

export const SVGContainer = styled.div`
  right: 0;
  overflow-x: scroll;
  z-index: 1;
  width: 100%;
  position: relative;
  overflow-y: hidden;
`;

export const StyledUL = styled.ul`
  list-style: none;
  margin-block-end: 0px;
  padding-inline-start: 0px;
  > li {
    padding: 32px 20px 4px;
    text-align: right;
    border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
    margin-left: 32px;
  }
  > div > li {
    padding: 32px 20px 4px;
    text-align: right;
    border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
    margin-left: 32px;
  }
  > li:nth-child(1) {
    color: #7c7d79;
    font-size: 13px;
    font-weight: 500;
  }
  > div > li {
    font-weight: 900;
    color: ${({ theme }) => theme.color};
  }
`;

const convertAcademicYear = (year: string) => {
  const [startYear, endYear] = year?.split("/");
  const lastTwoDigitsStart = startYear?.slice(-2);
  const lastTwoDigitsEnd = endYear?.slice(-2);
  return `${lastTwoDigitsStart}/${lastTwoDigitsEnd}`;
};

const TransferChart: React.FC<TransferChartProps> = ({ propData = [], playerTransferData  }) => {
  const data: ITransferHistory[] = [...dummyTransferHistoryData, ...propData];
  const { theme } = useTheme();
  const barWidth = 80;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const scrollRef = useHorizontalScroll();
  const totalBarGroupWidth = data !== null ? data.length * barWidth : barWidth;
  const adjustedChartWidth = Math.max(scrollRef.current?.clientWidth!, totalBarGroupWidth);

  function getInjuryWeekLabels(injuryWeeks: number[], numLabels: number) {
    if (injuryWeeks.length > 0) {
      const maxWeek = Math.max(...injuryWeeks);
      const idealIntervalWidth = (maxWeek + 1) / (numLabels - 1);
      const labels = [];
      for (let i = 0; i < numLabels; i++) {
        let value: number;
        if (i === 0) {
          value = 0;
        } else if (i === numLabels - 1) {
          value = maxWeek;
        } else {
          value = Math.round(idealIntervalWidth * i);
        }
        labels.push(value.toString());
      }
      return labels;
    }
  }

  const injuryWeeks = data?.slice(2)?.map((d) => d.wagesPW) ?? [20, 70, 120, 170, 220, 270];
  const labels = injuryWeeks && getInjuryWeekLabels(injuryWeeks as number[], 5);
  useEffect(() => {
    if (data !== null) {
      const labelHeight = (document.getElementsByClassName("side-transfer-container")[0] as HTMLElement)?.getBoundingClientRect().height;
      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();
      const barGroup = svg.append("g").attr("height", labelHeight).attr("width", adjustedChartWidth);
      const colors = [theme.dropdownBorderColor, theme.shade2Chart];
      const hoverColors = [theme.shade1ChartHover, theme.shade2ChartHover];
      let barTooltip:NodeJS.Timeout;
      const tooltip = d3
        .select("body")
        .append("div")
        .style("position", "fixed")
        .style("z-index", 999)
        .style("visibility", "hidden")
        .style("background-color", theme.secondaryButtonBackground)
        .style("color", theme.color)
        .style("border", "1px solid ${theme.secondaryButtonBackground}")
        .style("padding", "8px")
        .style("border-radius", "4px")
        .style("box-shadow", "0px 0px 6px rgba(0, 0, 0, 0.1)");

      if (data.length > 20) {
        data.forEach((_, i) => {
          barGroup
            .append("rect")
            .attr("width", barWidth)
            .classed("bar", true)
            .attr("height", labelHeight)
            .attr("x", i * barWidth)
            .attr("y", 0)
            .style("fill", colors[i % 2])
            .style("cursor", "pointer")
            .on("mouseover", (event) => {
              d3.select(event.target).style("fill", hoverColors[i % 2]);
              const transferTexts = svg.selectAll(`.transferText${i}`).nodes();
              const transferWidths = transferTexts.map((_, i, nodes) => {
                const textElement = nodes[i];
                // @ts-ignore
                const width = textElement?.getComputedTextLength();
                return width;
              });
              barTooltip = setTimeout(() => {
                svg
                .select(`.transferTextContainer${i}`)
                .attr("visibility", "visible")
                .attr("width", Math.max(...transferWidths) + 35);
                svg.selectAll(`.transferText${i}`).attr("visibility", "visible");
              }, 500)
            })
            .on("mouseout", (event) => {
              clearTimeout(barTooltip);
              d3.select(event.target).style("fill", colors[i % 2]);
              svg.select(`.transferTextContainer${i}`).attr("visibility", "hidden");
              svg.selectAll(`.transferText${i}`).attr("visibility", "hidden");
            });
        });
      } else {
        const numBars = adjustedChartWidth / barWidth + 1;
        for (let i = 0; i < numBars; i++) {
          barGroup
            .append("rect")
            .attr("width", barWidth)
            .classed("bar", true)
            .attr("height", labelHeight)
            .attr("x", i * barWidth)
            .attr("y", 0)
            .style("fill", colors[i % 2])
            .style("cursor", "pointer")
            .on("mouseover", (event) => {
              d3.select(event.target).style("fill", hoverColors[i % 2]);
              const transferTexts = svg.selectAll(`.transferText${i}`).nodes();
              const transferWidths = transferTexts.map((_, i, nodes) => {
                const textElement = nodes[i];
                // @ts-ignore
                const width = textElement?.getComputedTextLength();
                return width;
              });
              barTooltip = setTimeout(() => {
                svg
                .select(`.transferTextContainer${i}`)
                .attr("visibility", "visible")
                .attr("width", Math.max(...transferWidths) + 35);
                svg.selectAll(`.transferText${i}`).attr("visibility", "visible");
              }, 500)
            })
            .on("mouseout", (event) => {
              clearTimeout(barTooltip);
              d3.select(event.target).style("fill", colors[i % 2]);
              svg.select(`.transferTextContainer${i}`).attr("visibility", "hidden");
              svg.selectAll(`.transferText${i}`).attr("visibility", "hidden");
            });
        }
      }

      data?.forEach((item, i) => {
        svg
          .append("text")
          .text(item.season ? (item.season.includes("/") ? convertAcademicYear(item.season) : item.season) : "")
          .style("fill", theme.NumberColor)
          .style("font-family", "DM Mono")
          .style("font-size", "14px")
          .style("font-weight", 500)
          .attr("x", i * barWidth + barWidth / 2 - (item.season.includes("/") ? 20 : 18))
          .attr("y", 343); // Position of the season label
        svg
          .append("text")
          .text(item.age ? item.age : "")
          .style("fill", theme.NumberColor)
          .style("font-family", "DM Mono")
          .style("font-size", "14px")
          .style("font-weight", 500)
          .attr("x", i * barWidth + barWidth / 2 - 10)
          .attr("y", 371); // Adjust this y position to be under the season label
        
        let clubTooltip:NodeJS.Timeout;
        // Append club logos
        if (item.teamLogoURL) {
          svg
            .append("image")
            .attr("href", String(item.teamLogoURL))
            .attr("width", 32)
            .attr("height", 32)
            .attr("x", i * barWidth + barWidth / 2 - 16)
            .attr("y", 390)
            .style("cursor", "pointer")
            .on("mouseover", (event, d) => {
              clubTooltip = setTimeout(() => {
                tooltip.style("visibility", "visible").text(`${item.teamName}`);
              },500)
            })
            .on("mousemove", (event) => {
              tooltip.style("left", `${event.clientX - 50}px`).style("top", `${event.clientY - 50}px`);
            })
            .on("mouseout", () => {
              clearTimeout(clubTooltip);
              tooltip.style("visibility", "hidden");
            });
          {
            item?.playerTransfers?.length > 0 &&
              svg
                .append("circle")
                .style("fill", "#C9F0B5")
                .attr("r", 8)
                .attr("cx", i * barWidth + barWidth / 2 + 10)
                .attr("cy", 390)
                .attr("pointer-events", "none");
          }
          {
            item?.playerTransfers?.length > 0 &&
              svg
                .append("text")
                .style("fill", "#11111")
                .text(item?.playerTransfers?.length)
                .attr("x", i * barWidth + barWidth / 2 + 6.5)
                .attr("y", 395)
                .attr("pointer-events", "none");
          }
        }
      });

        for (let i = 0; i < 6; i++) {
          barGroup
            .append("line")
            .attr("x1", 0)
            .attr("x2", adjustedChartWidth)
            .attr("y1", (labelHeight / 5) * i)
            .attr("y2", (labelHeight / 5) * i)
            .attr("stroke-width", 1)
            .attr("stroke", theme.playerDataBorder);
        }

        const xScale = d3
          .scaleLinear()
          .domain([0, Math.max(...data.map((d) => d.wagesPW))])
          .range([0, (labelHeight / 5) * 4]);

        const filter = svg.append("filter").attr("id", "box-shadow");
        filter.append("feDropShadow").attr("dx", 2).attr("dy", 2).attr("stdDeviation", 2).attr("flood-color", "gray");

        data?.forEach((item, i) => {
          if (i > 0) {
            const allTransfers = item?.playerTransfers;
            barGroup
              .append("rect")
              .attr("class", `wageBar${i}`)
              .attr("width", barWidth - 48)
              .attr("height", !item.wagesPW || item.wagesPW === 0 ? 10 : xScale(item.wagesPW))
              .attr("x", i * barWidth + 24 + (allTransfers?.length ? allTransfers?.length * 2.5 : 0))
              .attr("y", labelHeight - (!item.wagesPW || item.wagesPW === 0 ? 10.5 : xScale(item.wagesPW)))
              .style("fill", !item.wagesPW || item.wagesPW === 0 ? colors[i % 2] : "#FFC2C2")
              .attr("rx", !item.wagesPW || item.wagesPW === 0 ? 2 : 5)
              .style("stroke", !item.wagesPW || item.wagesPW === 0 ? theme.NumberColor : "")
              .style("stroke-width", !item.wagesPW || item.wagesPW === 0 ? 1 : 0)
              .attr("pointer-events", "none");
            barGroup
              .append("rect")
              .attr("width", barWidth - 48)
              .attr("height", !item.wagesPW || item.wagesPW === 0 ? 10 : xScale(item.wagesPW) - 10)
              .attr("x", i * barWidth + 24 + (allTransfers?.length ? allTransfers?.length * 2.5 : 0))
              .attr("y", labelHeight - (!item.wagesPW || item.wagesPW === 0 ? 20 : xScale(item.wagesPW)) + 10)
              .style("fill", !item.wagesPW || item.wagesPW === 0 ? "transparent" : "#FFC2C2")
              .attr("pointer-events", "none");
            allTransfers?.map((transfer, j) => {
              barGroup
                .append("rect")
                .attr("width", 2)
                .attr("height", transfer.transferFeeValue ? xScale(transfer.transferFeeValue) : xScale(1800000))
                .attr("x", i * barWidth + 19 - j * 5 + allTransfers?.length * 2.5)
                .attr("y", labelHeight - (transfer.transferFeeValue ? xScale(transfer.transferFeeValue) : xScale(1800000)))
                .style("fill", "#00D1A8")
                .attr("pointer-events", "none");
              svg
                .append("rect")
                .attr("width", transfer?.transferFeeValueInText?.length > 3 ? 43 : 30)
                .attr("height", 15)
                .attr(
                  "x",
                  i * barWidth + 19 - j * 5 + allTransfers?.length * 2.5 - (transfer?.transferFeeValueInText?.length > 3 ? 18 : 14)
                )
                .attr("y", labelHeight - 80 - j * 18)
                .style("fill", "white")
                .style("stroke", "#00D1A8")
                .style("stroke-width", 1)
                .attr("rx", 2)
                .attr("pointer-events", "none");
              svg
                .append("text")
                .text(
                  `€${transfer.isFreeTransfer ? "0M" : transfer.transferFeeValueInText === "0" ? "0M" : transfer.transferFeeValueInText}`
                )
                .attr(
                  "x",
                  i * barWidth +
                    19 -
                    j * 5 +
                    allTransfers?.length * 2.5 -
                    (transfer?.transferFeeValueInText?.length > 3 ? 15 : transfer?.transferFeeValueInText?.length > 2 ? 13 : 11)
                )
                .attr("y", labelHeight - 69 - j * 18)
                .style("font-family", "Aeonik")
                .style("font-size", "11px")
                .style("font-weight", 500)
                .attr("pointer-events", "none");
            });
          }
        });

        data?.forEach((item, i) => {
          if (i > 0) {
            const allTransfers = item?.playerTransfers;
            svg
              .append("rect")
              .attr("class", `transferTextContainer${i}`)
              .attr("width", "120")
              .attr("height", !allTransfers ? 40 : allTransfers?.length * 15 + 30)
              .attr("x", i * barWidth - (!allTransfers ? 5 : 25))
              .attr(
                "y",
                labelHeight -
                  xScale(item.wagesPW ? item.wagesPW : 20) -
                  (!allTransfers ? 50 : 35) -
                  (allTransfers && allTransfers?.length * 15)
              )
              .attr("fill", "white")
              .attr("rx", 2)
              .attr("filter", "url(#box-shadow)")
              .attr("visibility", "hidden")
              .attr("pointer-events", "none");

            allTransfers?.map((transfer, j) => {
              if (i > 0) {
                svg
                  .append("text")
                  .attr("class", `transferText${i}`)
                  .text(
                    `${transfer.isFreeTransfer ? "" : "€"}${
                      !transfer.isFreeTransfer
                        ? transfer.transferFeeValueInText === "0"
                          ? `${transfer.transferFeeValueInText}M`
                          : transfer.transferFeeValueInText
                        : "Free"
                    } transfer ${!transfer.isFreeTransfer ? "fee" : ""} ${transfer.loanStatus === "Start" ? "(loan)" : ""} to ${
                      transfer.teamName
                    }`
                  )
                  .attr("x", i * barWidth - 15)
                  .attr("y", labelHeight - xScale(item.wagesPW ? item.wagesPW : 20) - allTransfers?.length * 15 - 18 + j * 16)
                  .style("font-family", "Aeonik")
                  .style("font-size", "12px")
                  .style("font-weight", 500)
                  .attr("pointer-events", "none")
                  .attr("visibility", "hidden");
              }
            });
            if (!allTransfers) {
              svg
                .append("text")
                .attr("class", `transferText${i}`)
                .text(`Transfer fee data not available`)
                .attr("x", i * barWidth + 5)
                .attr("y", labelHeight - xScale(item.wagesPW ? item.wagesPW : 20) - 33)
                .style("font-family", "Aeonik")
                .style("font-size", "12px")
                .style("font-weight", 500)
                .attr("pointer-events", "none")
                .attr("visibility", "hidden");
              svg
                .append("text")
                .attr("class", `transferText${i}`)
                .text(item.wagesPWText ? `${item.wagesPWText} wages pw` : `Wages data not available`)
                .attr("x", i * barWidth + 5)
                .attr("y", labelHeight - xScale(item.wagesPW ? item.wagesPW : 20) - 18)
                .style("font-family", "Aeonik")
                .style("font-size", "12px")
                .style("font-weight", 500)
                .attr("pointer-events", "none")
                .attr("visibility", "hidden");
            }
            if (allTransfers) {
              svg
                .append("text")
                .attr("class", `transferText${i}`)
                .text(item.wagesPWText ? `${item.wagesPWText} wages pw` : `Wages data not available`)
                .attr("x", i * barWidth - 15)
                .attr("y", labelHeight - xScale(item.wagesPW ? item.wagesPW : 20) - 13)
                .style("font-family", "Aeonik")
                .style("font-size", "12px")
                .style("font-weight", 500)
                .attr("pointer-events", "none")
                .attr("visibility", "hidden");
            }
          }
        });
      }
    
  }, [data, theme, adjustedChartWidth]);

  return (
    <>
      <StyledHeader className="header-content" style={{ paddingTop: "14px", paddingBottom: "14px" }}>
        <LabelText className="player-family-name">Transfer & contracts</LabelText>
      </StyledHeader>
      <ChartContainer>
        <div className="transfer-dotlabel-container">
          <p>PUBLISHED TRANSFER FEE</p>
          <p>WAGES PW</p>
        </div>
        <SideBar>
          <StyledUL>
            <li>VALUE</li>
            <div className="side-transfer-container">
              <>
                {labels !== undefined &&
                  labels.reverse().map((label) =>
                    playerTransferData?.length === 0 ? (
                      <li className="side-label"></li>
                    ) : (
                      <li className="side-label" key={label}>
                        {label.length > 2 ? label.slice(0, -3) : label}K
                      </li>
                    )
                  )}
              </>
            </div>
          </StyledUL>
          <SidebarLower>
            <LabelNumber className="attack-defense-data">SEASON</LabelNumber>
            <LabelNumber className="attack-defense-data">AGE</LabelNumber>
            <LabelNumber className="attack-defense-data">CLUB</LabelNumber>
          </SidebarLower>
        </SideBar>
        {/* @ts-ignore */}
        <SVGContainer ref={scrollRef} id="injuryContainer" className="injury-chart-container">
          <svg
            ref={svgRef}
            style={{
              position: "absolute",
              top: "78px", //78
              height: "425px",
              width: adjustedChartWidth, // Use dynamic width
            }}
          ></svg>
          {propData?.length === 0 && <div className="data-not-available-circle">
            <div>
              Data not currently available
            </div>
          </div> }
        </SVGContainer>
      </ChartContainer>
    </>
  );
};

export default TransferChart;
